import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { HeaderToolsItem } from "@ui/eva-components/MobileFirstHeader/components/HeaderToolsItem";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useViewport } from "@ui/hooks/useViewport";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { Icon } from "@jti/core-components/components/Icon";
import { extractLanguageCode, setCookie } from "@jti/fe-common";

import { LanguageSelectorPopupDesktop } from "./components/LanguageSelectorPopupDesktop";
import { LanguageSelectorPopupMobile } from "./components/LanguageSelectorPopupMobile";
import { LanguageSelectorPopupMobileFullscreen } from "./components/LanguageSelectorPopupMobileFullscreen";
import { LanguageSelectorButton } from "./components/LanguageSelectorPopupMobileFullscreen/components/LanguageSelectorButton";

import styles from "./LanguageSelector.module.scss";

export type Language = {
  current: boolean;
  path: string;
  associatedCookieValue: string;
  languageDisplayName: string;
};

export type PopupContext = "NAVIGATION" | "AGE_CONFIRMATION";

type LanguageSelectorProps = {
  languages: Language[];
  variant: "FULL_LANGUAGE_NAME" | "SHORT_LANGUAGE_NAME" | "CUSTOM_NAME";
  popupContext: PopupContext;
  customName?: string;
  customClassName?: string;
  size?: "compact";
};

const MOBILE_WIDTH = 768;

export const calculateExpireDate = (): Date => {
  const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
  const oneYearAhead = new Date(Date.now() + oneYearInMs);
  return oneYearAhead;
};

export const setLanguageCookie = (languageCode: string): void => {
  setCookie("language", languageCode, "/", calculateExpireDate().toUTCString());
};

export const LanguageSelector: FC<LanguageSelectorProps> = ({
  languages,
  variant,
  popupContext,
  customName = "",
  customClassName = "",
  size,
}) => {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewport = useViewport();
  const currentLanguage = useMemo(() => languages.find(({ current }) => current), [languages]);
  const { __ } = useTranslations();

  const { isMobileFullscreenLanguageSelectorEnabled, isMobileFirstNavigationEnabled } = useConfig();

  const [isInsideHeader, setIsInsideHeader] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      const isInside = !!containerRef.current.closest("header");
      setIsInsideHeader(isInside);
    }
  }, []);

  useOutsideClick(containerRef, () => {
    if (viewport.width >= MOBILE_WIDTH) {
      setIsOpened(false);
    }
  });

  const showLanguageSelectorButton =
    viewport.width < MOBILE_WIDTH && isInsideHeader && isMobileFullscreenLanguageSelectorEnabled;

  const handleChange = ({ associatedCookieValue, path }: Language) => {
    setLanguageCookie(associatedCookieValue);
    window.location.assign(path + window.location.search);
  };

  const renderLanguageName = () => {
    if (variant === "FULL_LANGUAGE_NAME") {
      return currentLanguage?.languageDisplayName;
    }
    if (variant === "CUSTOM_NAME") {
      return customName;
    }
    return extractLanguageCode(currentLanguage?.associatedCookieValue ?? "");
  };

  const toggleDropdown = () => {
    setIsOpened((isOpened) => !isOpened);
  };

  const closePopup = () => {
    setIsOpened(false);
  };

  useEffect(() => {
    if (currentLanguage && location.search?.includes("redirect")) {
      setLanguageCookie(currentLanguage.associatedCookieValue);
    }
  }, [currentLanguage, languages]);

  if (languages.length < 2) {
    return null;
  }

  const mobilePopup = isMobileFullscreenLanguageSelectorEnabled ? (
    <LanguageSelectorPopupMobileFullscreen
      isOpened={isOpened}
      onApplyLanguage={handleChange}
      onClose={closePopup}
      languages={languages}
      popupContext={popupContext}
    />
  ) : (
    <LanguageSelectorPopupMobile
      isOpened={isOpened}
      onApplyLanguage={handleChange}
      onClose={closePopup}
      languages={languages}
      popupContext={popupContext}
    />
  );

  return (
    <div
      data-testid="languageSelectorContainer"
      ref={containerRef}
      className={classNames(styles.languageSelector, size && styles[size], customClassName, {
        [styles.languageSelectorOpened]: isOpened,
      })}
    >
      {showLanguageSelectorButton ? (
        <LanguageSelectorButton toggleDropdown={toggleDropdown} languageName={renderLanguageName() ?? ""} />
      ) : (
        <HeaderToolsItem
          onClick={toggleDropdown}
          data-testid="languageSelectorDropdown"
          className={
            isMobileFirstNavigationEnabled ? styles.languageSelectorDropdown : styles.legacyLanguageSelectorDropdown
          }
          aria-label={__("languageselector.toggle")}
        >
          <span data-testid="languageSelectorDropdownText">{renderLanguageName()}</span>
          <Icon icon={isOpened ? ChevronUpIcon : ChevronDownIcon} size="xs" />
        </HeaderToolsItem>
      )}
      {viewport.width >= MOBILE_WIDTH ? (
        <LanguageSelectorPopupDesktop isOpened={isOpened} onSelectLanguage={handleChange} languages={languages} />
      ) : (
        mobilePopup
      )}
    </div>
  );
};
